import { z } from 'zod';
import { MediaTypeEnum } from './media-unit-variants.js';
import { UnitBaseStatusSchema } from './reservation-status.js';
import { mediaTypeRequiresDimensions } from '../import/adapters/registry.js';

// ─── Allowed enum values ──────────────────────────────────────────────────────

export const MATERIAL_VALUES = [
  'Plakat',
  'Baner',
  'Baner blockout',
  'Backlight',
  'Siatka mesh',
  'Folia',
  'Folia OWV',
  'Mural',
  'brak',
] as const;
export const LIGHTING_VALUES = ['Tak', 'Nie', 'Tak (solarne)'] as const;

// ─── CreateMediaUnitCostDto ───────────────────────────────────────────────────

/**
 * Cost fields for a media unit. All fields are optional.
 * Covers both internal cost fields and client-facing price fields.
 * All provided values must be non-negative.
 */
export const CreateMediaUnitCostDto = z.object({
  // Internal cost fields
  catalogPrice: z.number().nonnegative().optional(),
  rent: z.number().nonnegative().optional(),
  exposureCost: z.number().nonnegative().optional(),
  productionCost: z.number().nonnegative().optional(),
  installCost: z.number().nonnegative().optional(),
  dismantlingCost: z.number().nonnegative().optional(),
  additionalCosts: z.number().nonnegative().optional(),
  // Client-facing price fields
  starterPrice: z.number().nonnegative().optional(),
  clientProduction: z.number().nonnegative().optional(),
  clientInstallation: z.number().nonnegative().optional(),
  clientDismantling: z.number().nonnegative().optional(),
  clientAdditionalFees: z.number().nonnegative().optional(),
  muralEcoProduction: z.number().nonnegative().optional(),
});

export type CreateMediaUnitCost = z.infer<typeof CreateMediaUnitCostDto>;

// ─── CreateMediaUnitVariantDto ────────────────────────────────────────────────

/**
 * A single variant for a media unit being created.
 * mediaType is required; all type-specific fields are optional.
 */
export const CreateMediaUnitVariantDto = z.object({
  mediaType: MediaTypeEnum,
  // General (multi-type)
  videocardLink: z.string().optional(),
  // Mural-specific
  muralCostStandard: z.number().nonnegative().optional(),
  muralCostAntismog: z.number().nonnegative().optional(),
  muralElement3d: z.boolean().optional(),
  muralLedTape: z.boolean().optional(),
  muralNeonLed: z.boolean().optional(),
  muralFluoUv: z.boolean().optional(),
  // Konstrukcja / highway-specific
  roadNumber: z.string().optional(),
  // Digital-specific
  digitalSpotDurationSeconds: z.number().int().nonnegative().optional(),
  digitalLoopSize: z.number().int().nonnegative().optional(),
  digitalScreenResolution: z.string().optional(),
  digitalScreenHours: z.string().optional(),
  digitalFrequency: z.string().optional(),
  digitalDailyEmissions: z.number().int().nonnegative().optional(),
  digitalTotalCampaignEmissions: z.number().int().nonnegative().optional(),
  digitalSpotPrice: z.number().nonnegative().optional(),
  // DOOH screen-specific (dooh-import-template capability)
  screensCount: z.number().int().nonnegative().optional(),
  isOutdoor: z.boolean().optional(),
  digitalScreenSizeInches: z.number().nonnegative().optional(),
  // MPK-specific
  mpkVehicleType: z.string().optional(),
  mpkLineNumbers: z.string().optional(),
  mpkPlacementOnVehicle: z.string().optional(),
  mpkVehicleCount: z.number().int().nonnegative().optional(),
});

export type CreateMediaUnitVariant = z.infer<typeof CreateMediaUnitVariantDto>;

// ─── CreateMediaUnitDto ───────────────────────────────────────────────────────

export const CreateMediaUnitDto = z
  .object({
    // Required fields
    supplierNumber: z.string().min(1),
    supplierId: z.number().int().positive().nullable(),
    formatId: z.number().int().positive(),
    regionCode: z.string().min(1),
    city: z.string().min(1),
    address: z.string().min(1),
    // Required only when at least one variant's mediaType requires dimensions
    // per the import adapter rule — see superRefine below (CU-12472jzd1eb).
    widthM: z.number().positive().optional(),
    heightM: z.number().positive().optional(),

    // Optional fields
    countyName: z.string().optional(),
    agglomeration: z.string().optional(),
    material: z.enum(MATERIAL_VALUES).optional(),
    lighting: z.enum(LIGHTING_VALUES).optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    notes: z.string().optional(),
    wfContactPerson: z.string().optional(),
    locationDescription: z.string().optional(),
    // Optional manual override for grw_id (unique ID). When omitted a deterministic ID is generated.
    grwId: z.string().optional(),
    status: UnitBaseStatusSchema.optional(),

    // Cost fields (sourced from CreateMediaUnitCostDto to avoid duplication)
    ...CreateMediaUnitCostDto.shape,

    // Variants array — at least one variant is required on creation.
    variants: z.array(CreateMediaUnitVariantDto).min(1),
  })
  .superRefine((data, ctx) => {
    // Bundles mix variant types on one unit; requiring dimensions if ANY
    // variant needs them keeps the shared widthM/heightM columns populated
    // for whichever variant actually needs them (product decision CU-12472jzd1eb).
    if (!data.variants.some((v) => mediaTypeRequiresDimensions(v.mediaType))) return;
    if (data.widthM === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['widthM'], message: 'Required' });
    }
    if (data.heightM === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['heightM'], message: 'Required' });
    }
  });

export type CreateMediaUnit = z.infer<typeof CreateMediaUnitDto>;
